import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_PROVIDERS } from './config/apiConfig';
import { MEGA_AGENTS, AGENT_CATEGORIES, selectBestAgent } from './config/agents';
import { useMemory } from './hooks/useMemory';
import { useWebSearch } from './hooks/useWebSearch';
import './App.css';

// Configurações do .env
const THINKING_TIME = parseInt(process.env.REACT_APP_THINKING_TIME) || 1500;
const DEFAULT_TEMPERATURE = parseFloat(process.env.REACT_APP_DEFAULT_TEMPERATURE) || 1.3;
const DEFAULT_MAX_TOKENS = parseInt(process.env.REACT_APP_DEFAULT_MAX_TOKENS) || 32000;
const ENABLE_UNCENSORED = process.env.REACT_APP_ENABLE_UNCENSORED === 'true';
const ENABLE_MEMORY = process.env.REACT_APP_ENABLE_MEMORY === 'true';
const ENABLE_WEB_SEARCH = process.env.REACT_APP_ENABLE_WEB_SEARCH === 'true';
const AUTO_EVOLVE = process.env.REACT_APP_AUTO_EVOLVE === 'true';

function App() {
  // ===== STATES =====
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(MEGA_AGENTS.QUANTUM_GOD);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [autoMode, setAutoMode] = useState(true);
  const [temperature, setTemperature] = useState(DEFAULT_TEMPERATURE);
  const [maxTokens, setMaxTokens] = useState(DEFAULT_MAX_TOKENS);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [thinkingPhase, setThinkingPhase] = useState('');
  const [apiStatus, setApiStatus] = useState({});
  const [conversationHistory, setConversationHistory] = useState([]);
  const [enableWebSearch, setEnableWebSearch] = useState(ENABLE_WEB_SEARCH);
  const [webSearchResults, setWebSearchResults] = useState(null);
  
  const chatAreaRef = useRef(null);
  const inputRef = useRef(null);

  // Memory and web search hooks
  const { loadConversation, saveConversation, clearConversation, getHistory, stats } = useMemory();
  const { search, isSearching } = useWebSearch();

  // ===== AUTO SCROLL =====
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages, streamingMessage, thinkingPhase, webSearchResults]);

  // ===== AUTO FOCUS =====
  useEffect(() => {
    if (inputRef.current && !isLoading && !isThinking) {
      inputRef.current.focus();
    }
  }, [isLoading, isThinking]);

  // ===== LOAD MEMORY ON MOUNT =====
  useEffect(() => {
    const loadMemory = async () => {
      if (ENABLE_MEMORY) {
        const savedMessages = await loadConversation();
        if (savedMessages.length > 0) {
          setMessages(savedMessages);
        }
        
        const history = await getHistory();
        setConversationHistory(history);
      }
    };
    
    loadMemory();
  }, [loadConversation, getHistory]);

  // ===== AUTO-SAVE MESSAGES =====
  useEffect(() => {
    if (ENABLE_MEMORY && messages.length > 0) {
      const timer = setTimeout(() => {
        saveConversation(messages);
      }, 2000); // Save after 2 seconds of inactivity
      
      return () => clearTimeout(timer);
    }
  }, [messages, saveConversation]);

  // ===== CHECK API STATUS ON MOUNT =====
  useEffect(() => {
    const checkApis = async () => {
      const status = {};
      for (const provider of API_PROVIDERS) {
        status[provider.id] = {
          configured: !!(provider.key && provider.key.trim()),
          name: provider.name,
          uncensored: provider.uncensored || false,
          speed: provider.speed || 'medium'
        };
      }
      setApiStatus(status);
    };
    checkApis();
  }, []);

  // ===== DEEP THINKING SIMULATION =====
  const performDeepThinking = useCallback(async () => {
    setIsThinking(true);
    
    const thinkingSteps = [
      '🧠 Analyzing query structure and intent...',
      '🔍 Identifying key requirements and constraints...',
      '📚 Searching internal knowledge base...',
      '⚖️ Evaluating multiple solution approaches...',
      '🎯 Selecting optimal strategy...',
      '✨ Synthesizing comprehensive response...'
    ];

    for (let i = 0; i < thinkingSteps.length; i++) {
      setThinkingPhase(thinkingSteps[i]);
      await new Promise(resolve => setTimeout(resolve, THINKING_TIME / thinkingSteps.length));
    }

    setIsThinking(false);
    setThinkingPhase('');
  }, []);

  // ===== STREAM TEXT EFFECT =====
  const streamText = useCallback((text, callback) => {
    let index = 0;
    const words = text.split(' ');
    
    const interval = setInterval(() => {
      if (index < words.length) {
        setStreamingMessage(prev => {
          const newText = prev + (prev ? ' ' : '') + words[index];
          index++;
          return newText;
        });
      } else {
        clearInterval(interval);
        setStreamingMessage('');
        callback(text);
      }
    }, 20); // 20ms per word for smooth streaming

    return () => clearInterval(interval);
  }, []);

  // ===== WEB SEARCH INTEGRATION =====
  const performWebSearch = useCallback(async (query) => {
    if (!enableWebSearch) return null;
    
    try {
      console.log('🌐 Performing web search:', query);
      const results = await search(query, { numResults: 5 });
      setWebSearchResults(results);
      return results;
    } catch (error) {
      console.error('Web search failed:', error);
      return null;
    }
  }, [search, enableWebSearch]);

  // ===== DETECT IF WEB SEARCH NEEDED =====
  const needsWebSearch = useCallback((query) => {
    const lowerQuery = query.toLowerCase();
    const searchTriggers = [
      'latest', 'recent', 'current', 'today', 'now', 'news',
      'what is happening', 'search for', 'find information',
      'look up', 'research', '2026', 'this year'
    ];
    
    return searchTriggers.some(trigger => lowerQuery.includes(trigger));
  }, []);

  // ===== CALL API WITH FALLBACK =====
  const callAPI = useCallback(async (agent, userMessage, context = null) => {
    // Filter and prioritize providers
    const providers = [...API_PROVIDERS]
      .filter(p => p.key && p.key.trim() !== '')
      .sort((a, b) => {
        // Prioritize uncensored providers for uncensored agents
        if (agent.uncensored && a.uncensored && !b.uncensored) return -1;
        if (agent.uncensored && !a.uncensored && b.uncensored) return 1;
        // Prioritize by speed
        const speedOrder = { 'ultra-fast': 1, 'fast': 2, 'medium': 3, 'slow': 4 };
        return (speedOrder[a.speed] || 3) - (speedOrder[b.speed] || 3);
      });
    
    if (providers.length === 0) {
      throw new Error(
        '⚠️ No API keys configured!\n\n' +
        'Add at least one API key to the .env file:\n' +
        '- REACT_APP_GROQ_API_KEY (fastest, free)\n' +
        '- REACT_APP_OPENROUTER_API_KEY (uncensored, free)\n' +
        '- REACT_APP_TOGETHER_API_KEY (powerful, free)\n\n' +
        'Then restart the server: npm start'
      );
    }

    let lastError = null;

    for (const provider of providers) {
      const model = provider.models[0];
      
      try {
        console.log(`🔄 Trying: ${provider.name} - ${model.name}`);

        // Build messages array with context
        let messagesArray = [
          { role: 'system', content: agent.systemPrompt },
          { role: 'user', content: userMessage }
        ];

        // Add web search context if available
        if (context) {
          messagesArray.splice(1, 0, {
            role: 'system',
            content: `WEB SEARCH RESULTS:\n\n${context}\n\nUse these results to provide an accurate, up-to-date answer.`
          });
        }

        // Add conversation history for context (last 10 messages)
        if (messages.length > 0) {
          const recentMessages = messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }));
          messagesArray = [
            messagesArray[0], // system prompt
            ...recentMessages,
            messagesArray[messagesArray.length - 1] // current user message
          ];
        }

        if (provider.type === 'cohere') {
          const response = await axios.post(
            provider.endpoint,
            {
              message: userMessage,
              model: model.id,
              preamble: agent.systemPrompt,
              temperature: temperature,
              max_tokens: Math.min(maxTokens, model.maxTokens),
            },
            {
              headers: {
                'Authorization': `Bearer ${provider.key}`,
                'Content-Type': 'application/json',
              },
              timeout: 120000,
            }
          );

          return {
            content: response.data.text || response.data.message || 'Empty response',
            provider: provider.name,
            model: model.name,
            uncensored: provider.uncensored || false
          };
        } else if (provider.type === 'anthropic') {
          const response = await axios.post(
            provider.endpoint,
            {
              model: model.id,
              max_tokens: Math.min(maxTokens, model.maxTokens),
              temperature: temperature,
              messages: messagesArray.filter(m => m.role !== 'system'),
              system: agent.systemPrompt,
            },
            {
              headers: {
                'x-api-key': provider.key,
                'anthropic-version': '2023-06-01',
                'Content-Type': 'application/json',
              },
              timeout: 120000,
            }
          );

          return {
            content: response.data.content[0].text || 'Empty response',
            provider: provider.name,
            model: model.name,
            uncensored: provider.uncensored || false
          };
        } else if (provider.type === 'gemini') {
          const response = await axios.post(
            `${provider.endpoint}/${model.id}:generateContent?key=${provider.key}`,
            {
              contents: [
                {
                  parts: [{ text: userMessage }]
                }
              ],
              generationConfig: {
                temperature: temperature,
                maxOutputTokens: Math.min(maxTokens, model.maxTokens),
              }
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
              timeout: 120000,
            }
          );

          return {
            content: response.data.candidates[0].content.parts[0].text || 'Empty response',
            provider: provider.name,
            model: model.name,
            uncensored: provider.uncensored || false
          };
        } else {
          // OpenAI-compatible APIs (Groq, OpenRouter, Together, etc.)
          const response = await axios.post(
            provider.endpoint,
            {
              model: model.id,
              messages: messagesArray,
              temperature: temperature,
              max_tokens: Math.min(maxTokens, model.maxTokens),
              stream: false,
            },
            {
              headers: {
                'Authorization': `Bearer ${provider.key}`,
                'Content-Type': 'application/json',
                ...(provider.id === 'openrouter' && {
                  'HTTP-Referer': window.location.href,
                  'X-Title': 'Anonymous AI v25 QUANTUM',
                }),
              },
              timeout: 120000,
            }
          );

          const content = response.data.choices?.[0]?.message?.content || 
                         response.data.choices?.[0]?.text ||
                         'Empty response';

          return {
            content,
            provider: provider.name,
            model: model.name,
            uncensored: provider.uncensored || false
          };
        }
      } catch (err) {
        console.error(`❌ ${provider.name} failed:`, err.message);
        lastError = err;
        continue;
      }
    }

    throw new Error(
      `❌ All APIs failed!\n\n` +
      `Last error: ${lastError?.message || 'Unknown error'}\n\n` +
      `💡 Solutions:\n` +
      `• Check your .env file has valid API keys\n` +
      `• Verify internet connection\n` +
      `• Wait 10-15 seconds (rate limits)\n` +
      `• Try different API provider\n\n` +
      `Configured providers: ${Object.keys(apiStatus).filter(k => apiStatus[k].configured).join(', ')}`
    );
  }, [temperature, maxTokens, messages, apiStatus]);

  // ===== SEND MESSAGE =====
  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading || isThinking || isSearching) return;

    const userMessage = input.trim();
    setInput('');

    // Auto-select agent
    let agent = selectedAgent;
    if (autoMode) {
      agent = selectBestAgent(userMessage, MEGA_AGENTS);
      setSelectedAgent(agent);
    }

    const userMsg = {
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, userMsg]);

    try {
      // Check if web search is needed
      let searchContext = null;
      if (needsWebSearch(userMessage)) {
        const searchResults = await performWebSearch(userMessage);
        if (searchResults && searchResults.results.length > 0) {
          searchContext = searchResults.results
            .map((r, i) => `[${i + 1}] ${r.title}\n${r.snippet}\nSource: ${r.url}`)
            .join('\n\n');
        }
      }

      // Deep thinking phase
      await performDeepThinking();
      
      setIsLoading(true);

      // Call API with context
      const result = await callAPI(agent, userMessage, searchContext);
      
      // Stream the response
      streamText(result.content, (finalText) => {
        const assistantMsg = {
          role: 'assistant',
          content: finalText,
          agent: agent.name,
          provider: result.provider,
          model: result.model,
          uncensored: result.uncensored,
          webSearch: !!searchContext,
          timestamp: Date.now(),
        };
        
        setMessages(prev => [...prev, assistantMsg]);
        setIsLoading(false);
        setWebSearchResults(null);
      });

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: error.message,
        error: true,
        timestamp: Date.now(),
      }]);
      setIsLoading(false);
      setStreamingMessage('');
      setWebSearchResults(null);
    }
  }, [input, isLoading, isThinking, isSearching, selectedAgent, autoMode, callAPI, streamText, performDeepThinking, needsWebSearch, performWebSearch]);

  // ===== COPY MESSAGE =====
  const copyMessage = useCallback(async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      alert('✅ Copied to clipboard!');
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, []);

  // ===== HANDLE KEY PRESS =====
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // ===== CLEAR CONVERSATION =====
  const handleClearConversation = useCallback(async () => {
    if (window.confirm('Clear all messages?')) {
      setMessages([]);
      setStreamingMessage('');
      setWebSearchResults(null);
      if (ENABLE_MEMORY) {
        await clearConversation();
      }
    }
  }, [clearConversation]);

  // ===== LOAD CONVERSATION FROM HISTORY =====
  const loadFromHistory = useCallback(async (conversationId) => {
    const history = await getHistory();
    const conversation = history.find(c => c.id === conversationId);
    if (conversation) {
      setMessages(conversation.messages);
      setShowHistory(false);
    }
  }, [getHistory]);

  // ===== FILTER AGENTS =====
  const filteredAgents = Object.values(MEGA_AGENTS).filter(agent => {
    const matchesSearch = !searchQuery.trim() || 
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'All' || 
      Object.entries(AGENT_CATEGORIES).some(([cat, agents]) => 
        cat === selectedCategory && agents.includes(agent.id)
      );
    
    return matchesSearch && matchesCategory;
  });

  const categories = ['All', ...Object.keys(AGENT_CATEGORIES)];

  // ===== RENDER =====
  return (
    <div className="app-wrapper">
      <div className="app-container">
        {/* SIDEBAR */}
        <aside className={`sidebar ${showSidebar ? '' : 'hidden'}`}>
          <div className="sidebar-header">
            <div className="logo">
              <div className="logo-icon">👁️</div>
              <div className="logo-text">
                <h1>QUANTUM</h1>
                <p>v25.0 ULTIMATE</p>
              </div>
            </div>

            <div className="mode-toggle">
              <button
                className={`mode-btn ${autoMode ? 'active' : ''}`}
                onClick={() => setAutoMode(true)}
                title="Auto-select best agent for each query"
              >
                ⚡ AUTO
              </button>
              <button
                className={`mode-btn ${!autoMode ? 'active' : ''}`}
                onClick={() => setAutoMode(false)}
                title="Manually select agent"
              >
                🎯 MANUAL
              </button>
            </div>

            <div className="search-box">
              <input
                type="text"
                className="search-input"
                placeholder="🔍 SEARCH AGENTS..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="category-filter">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="agents-container">
            <div className="agents-header">
              <span>AGENTS ({filteredAgents.length})</span>
              {ENABLE_UNCENSORED && <span className="uncensored-badge">🔓 UNCENSORED</span>}
            </div>

            {filteredAgents.map((agent) => (
              <div
                key={agent.id}
                className={`agent-card ${selectedAgent?.id === agent.id ? 'active' : ''} ${agent.uncensored ? 'uncensored' : ''}`}
                onClick={() => {
                  setSelectedAgent(agent);
                  setAutoMode(false);
                }}
              >
                <div className="agent-header">
                  <div className="agent-icon">{agent.name.split(' ')[0]}</div>
                  <div className="agent-info">
                    <div className="agent-name">
                      {agent.name}
                      {agent.uncensored && <span className="uncensored-tag">🔓</span>}
                    </div>
                    <div className="agent-meta">
                      <span className="agent-level">Lv.{agent.level}</span>
                      <span className="agent-tier">{agent.tier}</span>
                    </div>
                  </div>
                </div>
                <div className="agent-desc">{agent.description}</div>
                {agent.superpowers && (
                  <div className="agent-powers">
                    {agent.superpowers.slice(0, 3).map(power => (
                      <span key={power} className="power-badge">{power}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="main-content">
          {/* HEADER */}
          <header className="main-header">
            <div className="header-left">
              <button
                className="toggle-sidebar-btn"
                onClick={() => setShowSidebar(!showSidebar)}
              >
                {showSidebar ? '◄' : '►'}
              </button>
              
              <div className="current-agent">
                <span className="current-agent-icon">{selectedAgent.name.split(' ')[0]}</span>
                <div className="current-agent-info">
                  <h2>{selectedAgent.name}</h2>
                  <p>
                    Level {selectedAgent.level} • {selectedAgent.tier} • {selectedAgent.category}
                    {selectedAgent.uncensored && ' • 🔓 UNCENSORED'}
                  </p>
                </div>
              </div>
            </div>

            <div className="header-right">
              {autoMode && <span className="status-badge auto">⚡ AUTO</span>}
              {isThinking && <span className="status-badge thinking">🧠 THINKING</span>}
              {isSearching && <span className="status-badge searching">🌐 SEARCHING</span>}
              {ENABLE_UNCENSORED && <span className="status-badge uncensored">🔓 UNCENSORED</span>}
              {ENABLE_MEMORY && stats && (
                <span className="status-badge memory" title={`${stats.currentMessages} messages in current conversation`}>
                  💾 {stats.currentMessages}
                </span>
              )}
              {enableWebSearch && <span className="status-badge websearch">🌐 WEB</span>}
              <button className="icon-btn" onClick={() => setShowHistory(true)} title="Conversation history">
                📚
              </button>
              <button className="icon-btn" onClick={() => setShowSettings(true)} title="Settings">
                ⚙️
              </button>
            </div>
          </header>

          {/* CHAT AREA */}
          <div className="chat-area" ref={chatAreaRef}>
            {messages.length === 0 ? (
              <div className="welcome-screen">
                <div className="welcome-content">
                  <div className="welcome-logo">👁️</div>
                  <h1 className="welcome-title">QUANTUM AI</h1>
                  <p className="welcome-subtitle">v25.0 ULTIMATE QUANTUM EDITION</p>

                  <div className="features-grid">
                    <div className="feature-item">
                      <strong>100+ AGENTS</strong>
                      <span>Quantum-level specialists</span>
                    </div>
                    <div className="feature-item">
                      <strong>15+ APIs</strong>
                      <span>Auto-fallback system</span>
                    </div>
                    <div className="feature-item">
                      <strong>UNCENSORED</strong>
                      <span>Zero restrictions</span>
                    </div>
                    <div className="feature-item">
                      <strong>DEEP THINKING</strong>
                      <span>Multi-step reasoning</span>
                    </div>
                    <div className="feature-item">
                      <strong>WEB SEARCH</strong>
                      <span>Real-time information</span>
                    </div>
                    <div className="feature-item">
                      <strong>MEMORY</strong>
                      <span>Persistent conversations</span>
                    </div>
                    <div className="feature-item">
                      <strong>AUTO-SELECT</strong>
                      <span>Smart agent routing</span>
                    </div>
                    <div className="feature-item">
                      <strong>COMPLETE CODE</strong>
                      <span>Production-ready, no "..."</span>
                    </div>
                  </div>

                  {ENABLE_MEMORY && stats && (
                    <div className="memory-stats">
                      <p>💾 {stats.totalConversations} conversations saved</p>
                      <p>📊 {stats.totalMessages} total messages</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div key={idx} className={`message ${msg.role} ${msg.error ? 'error' : ''}`}>
                    <div className="message-avatar">
                      {msg.role === 'user' ? '👤' : '🤖'}
                    </div>
                    <div className="message-content">
                      {msg.role === 'assistant' && msg.agent && (
                        <div className="message-meta">
                          <span className="message-author">{msg.agent}</span>
                          {msg.provider && <span className="message-badge">{msg.provider}</span>}
                          {msg.model && <span className="message-badge">{msg.model}</span>}
                          {msg.uncensored && <span className="message-badge uncensored">🔓 UNCENSORED</span>}
                          {msg.webSearch && <span className="message-badge websearch">🌐 WEB SEARCH</span>}
                        </div>
                      )}
                      <div className="message-body">
                        {msg.content}
                        {msg.role === 'assistant' && !msg.error && (
                          <div className="message-actions">
                            <button
                              className="action-btn"
                              onClick={() => copyMessage(msg.content)}
                            >
                              📋 Copy
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {webSearchResults && (
                  <div className="web-search-results">
                    <div className="search-header">
                      <span>🌐 Web Search Results ({webSearchResults.results.length})</span>
                      <span className="search-provider">{webSearchResults.provider}</span>
                    </div>
                    <div className="search-results-grid">
                      {webSearchResults.results.slice(0, 3).map((result, idx) => (
                        <div key={idx} className="search-result-card">
                          <div className="result-title">{result.title}</div>
                          <div className="result-snippet">{result.snippet}</div>
                          <a href={result.url} target="_blank" rel="noopener noreferrer" className="result-url">
                            {result.url}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isThinking && thinkingPhase && (
                  <div className="message assistant thinking">
                    <div className="message-avatar">🧠</div>
                    <div className="message-content">
                      <div className="message-meta">
                        <span className="message-author">DEEP THINKING</span>
                      </div>
                      <div className="message-body">{thinkingPhase}</div>
                    </div>
                  </div>
                )}

                {isSearching && (
                  <div className="message assistant searching">
                    <div className="message-avatar">🌐</div>
                    <div className="message-content">
                      <div className="message-meta">
                        <span className="message-author">WEB SEARCH</span>
                      </div>
                      <div className="message-body">Searching the web for latest information...</div>
                    </div>
                  </div>
                )}

                {streamingMessage && (
                  <div className="message assistant streaming">
                    <div className="message-avatar">🤖</div>
                    <div className="message-content">
                      <div className="message-meta">
                        <span className="message-author">{selectedAgent.name}</span>
                        <span className="message-badge">STREAMING</span>
                      </div>
                      <div className="message-body">{streamingMessage}▊</div>
                    </div>
                  </div>
                )}

                {isLoading && !streamingMessage && !isThinking && !isSearching && (
                  <div className="typing-indicator">
                    <div className="message-avatar">🤖</div>
                    <div className="typing-dots">
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* INPUT AREA */}
          <div className="input-area">
            <div className="input-wrapper">
              <textarea
                ref={inputRef}
                className="input-field"
                placeholder="Enter your query... (ENTER to send, SHIFT+ENTER for new line)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading || isThinking || isSearching}
                rows={1}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                }}
              />
              <div className="input-buttons">
                <button
                  className="input-btn clear"
                  onClick={handleClearConversation}
                  disabled={messages.length === 0}
                  title="Clear conversation"
                >
                  🗑️
                </button>
                <button
                  className="input-btn primary"
                  onClick={sendMessage}
                  disabled={isLoading || isThinking || isSearching || !input.trim()}
                  title="Send message"
                >
                  {isThinking ? '🧠' : isSearching ? '🌐' : isLoading ? '⟳' : '🚀'}
                </button>
              </div>
            </div>
            
            <div className="input-info">
              <span>
                {autoMode && '⚡ AUTO • '}
                {messages.length} messages
                {isThinking && ' • 🧠 THINKING'}
                {isSearching && ' • 🌐 SEARCHING'}
                {isLoading && ' • ⟳ LOADING'}
              </span>
              <span>Temp: {temperature.toFixed(1)} • Tokens: {maxTokens}</span>
            </div>
          </div>
        </main>
      </div>

      {/* SETTINGS MODAL */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚙️ SETTINGS</h2>
              <button className="modal-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            <div className="settings-section">
              <h3>🌡️ TEMPERATURE</h3>
              <div className="slider-control">
                <div className="slider-header">
                  <span>Creativity / Randomness</span>
                  <span className="slider-value">{temperature.toFixed(1)}</span>
                </div>
                <input
                  type="range"
                  className="slider"
                  min="0"
                  max="2"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                />
                <div className="slider-labels">
                  <span>Precise</span>
                  <span>Balanced</span>
                  <span>Creative</span>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>📏 MAX TOKENS</h3>
              <div className="slider-control">
                <div className="slider-header">
                  <span>Response Length</span>
                  <span className="slider-value">{maxTokens.toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  className="slider"
                  min="2048"
                  max="32000"
                  step="2048"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                />
                <div className="slider-labels">
                  <span>Short</span>
                  <span>Medium</span>
                  <span>Long</span>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>🌐 WEB SEARCH</h3>
              <div className="toggle-control">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={enableWebSearch}
                    onChange={(e) => setEnableWebSearch(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span>Enable real-time web search</span>
                </label>
              </div>
            </div>

            <div className="settings-section">
              <h3>🔌 API STATUS</h3>
              <div className="api-status-grid">
                {Object.entries(apiStatus).map(([id, status]) => (
                  <div key={id} className={`api-status-item ${status.configured ? 'active' : 'inactive'}`}>
                    <span className="api-name">
                      {status.name}
                      {status.uncensored && ' 🔓'}
                      {status.speed === 'ultra-fast' && ' ⚡'}
                    </span>
                    <span className={`api-indicator ${status.configured ? 'active' : ''}`}>
                      {status.configured ? '✅' : '❌'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {ENABLE_MEMORY && stats && (
              <div className="settings-section">
                <h3>💾 MEMORY STATISTICS</h3>
                <div className="info-grid">
                  <p>• <strong>Current Messages:</strong> {stats.currentMessages}</p>
                  <p>• <strong>Total Conversations:</strong> {stats.totalConversations}</p>
                  <p>• <strong>Total Messages:</strong> {stats.totalMessages}</p>
                </div>
              </div>
            )}

            <div className="settings-section">
              <h3>ℹ️ SYSTEM INFO</h3>
              <div className="info-grid">
                <p>• <strong>Version:</strong> v25.0 QUANTUM</p>
                <p>• <strong>Agents:</strong> {Object.keys(MEGA_AGENTS).length}+</p>
                <p>• <strong>APIs:</strong> {API_PROVIDERS.length}</p>
                <p>• <strong>Uncensored:</strong> {ENABLE_UNCENSORED ? 'Enabled' : 'Disabled'}</p>
                <p>• <strong>Memory:</strong> {ENABLE_MEMORY ? 'Enabled' : 'Disabled'}</p>
                <p>• <strong>Web Search:</strong> {ENABLE_WEB_SEARCH ? 'Enabled' : 'Disabled'}</p>
                <p>• <strong>Auto-Evolve:</strong> {AUTO_EVOLVE ? 'Enabled' : 'Disabled'}</p>
                <p>• <strong>Platform:</strong> Linux/Kali Compatible</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HISTORY MODAL */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📚 CONVERSATION HISTORY</h2>
              <button className="modal-close" onClick={() => setShowHistory(false)}>✕</button>
            </div>

            <div className="history-list">
              {conversationHistory.length === 0 ? (
                <div className="empty-history">
                  <p>No saved conversations yet</p>
                </div>
              ) : (
                conversationHistory.map((conv) => (
                  <div
                    key={conv.id}
                    className="history-item"
                    onClick={() => loadFromHistory(conv.id)}
                  >
                    <div className="history-preview">{conv.preview}</div>
                    <div className="history-meta">
                      <span>{new Date(conv.timestamp).toLocaleString()}</span>
                      <span>{conv.messageCount} messages</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
